import {
  queryOptions,
  useQuery,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

import { livePolling, prefetchRouteData } from '@/app/query-client'
import { indexSummaryQueryOptions } from '@/features/market/api/queries'
import { marketRailKeys } from '@/features/market-rail/api/keys'
import { commodityPricesService } from '@/features/market-rail/api/commodityPricesService'
import { railService } from '@/features/market-rail/api/railService'
import {
  SHARE_UNLOCKS_AVAILABLE,
  shareUnlocksService,
} from '@/features/market-rail/api/shareUnlocksService'
import type { RailMovers, RailUpdate, YearRange } from '@/features/market-rail/api/railSchemas'
import type { CommodityPrice } from '@/schemas/commodities'
import type { ShareUnlock } from '@/schemas/share-unlocks'
import { popularStocksQueryOptions } from '@/features/watchlist/api/queries'
import { marketStatusQueryOptions, useMarketStatus } from '@/hooks/useMarketStatus'

export const marketUpdatesQueryOptions = () =>
  queryOptions<RailUpdate[]>({
    queryKey: marketRailKeys.updates(),
    queryFn: ({ client }) => railService.updates(client),
  })

/**
 * The updates list.
 *
 * `queryFn: ({ client }) => ...` rather than a bare `railService.updates()`.
 * React Query hands the queryFn the QueryClient that owns the query, so (a)
 * the endpoint entries this writes land on the client the component is
 * actually mounted under, and (b) a test with its own `QueryClientProvider`
 * stays isolated instead of leaking through the module singleton that
 * `marketEndpoints()` falls back to — the pattern of
 * `hooks/useMarketStatus.ts:82`.
 *
 * Polling is gated on `useMarketStatus()` exactly as `useIndexSummary` and
 * `useMarketOverview` are, and for a sharper reason than most: the desk
 * publishes these during the session, so the list genuinely does go stale
 * while NEPSE trades, and genuinely does not once it has closed. Without the
 * gate a tab left open overnight would poll `/market/updates/` every 20
 * seconds until morning. `useMarketStatus` is itself a shared query
 * (`['market-status']`, its own root), so the subscribers the rail adds cost
 * no extra requests.
 */
export const useMarketUpdates = (): UseQueryResult<RailUpdate[]> => {
  const { isOpen } = useMarketStatus()

  return useQuery({ ...marketUpdatesQueryOptions(), refetchInterval: livePolling(isOpen) })
}

/**
 * "Prices today". `enabled` is the service's own switch: while mm-be has no endpoint the query
 * never runs, so nothing is fetched and no request can fail. Daily quotes, so no live polling.
 */
/** The Market rail's Top movers (2 + 2), off the same movers entries Overview reads. */
export const useRailMovers = (): UseQueryResult<RailMovers> => {
  const { isOpen } = useMarketStatus()

  return useQuery({
    queryKey: marketRailKeys.topMovers(),
    queryFn: ({ client }) => railService.topMovers(client),
    refetchInterval: livePolling(isOpen),
  })
}

/**
 * The Market rail's 52-week range. The year's bounds move once a session at most, so it polls
 * with the rest of the rail only while the market is open.
 */
export const useYearRange = (): UseQueryResult<YearRange | null> => {
  const { isOpen } = useMarketStatus()

  return useQuery({
    queryKey: marketRailKeys.yearRange(),
    queryFn: ({ client }) => railService.yearRange(client),
    refetchInterval: livePolling(isOpen),
  })
}

export const useCommodityPrices = (): UseQueryResult<CommodityPrice[]> =>
  useQuery({
    queryKey: marketRailKeys.commodities(),
    queryFn: () => commodityPricesService.rail(),
  })

/** "Shares unlocking soon", gated the same way. The list moves once a day at most. */
export const useShareUnlocks = (): UseQueryResult<ShareUnlock[]> =>
  useQuery({
    queryKey: marketRailKeys.shareUnlocks(),
    queryFn: () => shareUnlocksService.upcoming(),
    enabled: SHARE_UNLOCKS_AVAILABLE,
  })

/**
 * Starts every read the shared rail makes, plus market status — for the
 * `_app` layout's loader, so the rail is drawn from cache on the first screen
 * instead of popping in card by card after it (and `useRailFit` measures the
 * rail once, loaded, rather than again as each card lands).
 *
 * Not awaited, deliberately: no screen waits on its rail. The four cards keep
 * their own skeletons for the rare load where these are still out when the
 * page is ready, and their own errors.
 *
 * Polling is not set here — `prefetchRouteData` fetches once. Each card's
 * hook adds `refetchInterval` when it mounts, gated on market status exactly
 * as before; these factories carry only the key and the fetch, so the hook
 * and the prefetch cannot drift onto different keys.
 *
 * Market status is here, not in some screen's loader, because every live
 * query's poll is gated on it: fetched first, the gate is known before the
 * first live query mounts.
 *
 * The market mood (`marketMoodQueryOptions`) is deliberately NOT prefetched
 * here: only `/market` and its tool pages draw it (`FearGreedCard`), and this
 * runs on every screen, so a reading nothing on screen renders would just be
 * a wasted round trip.
 */
export const prefetchMarketRail = (client: QueryClient): void => {
  prefetchRouteData(client, marketStatusQueryOptions())
  prefetchRouteData(client, indexSummaryQueryOptions('live'))
  prefetchRouteData(client, marketUpdatesQueryOptions())
  prefetchRouteData(client, popularStocksQueryOptions())
}
